import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Grid,
  useMediaQuery,
  useTheme,
  Button
} from '@mui/material';
import { FaLink, FaUnlink, FaUser } from 'react-icons/fa';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import { useCV } from '../contexts/CVContext';
import Modal from '../../../common/Modal';
import RolesSelection from '../../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import { ROLE_TYPES } from '../../../../constants/roleTypes';
import { ORG_UNIT_TYPES } from '../../../../constants/radar';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmployeeSearch from '../../../common/EmployeeSearch';
import PositionGradeRolesBox from './common/PositionGradeRolesBox';

const PersonalDetails = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { isDataReady, cvData, updatePersonalDetails, isLoading, error, disableEmployeeChange, readonly } = useCV();

  // Ref to track if form has been initialized
  const isInitialized = useRef(false);
  
  // State for roles modal
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  
  // State for link employee modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  // State for custom classification
  const [useCustomClassification, setUseCustomClassification] = useState(false);
  const [customClassification, setCustomClassification] = useState('');
  
  // Calculate avatar size based on screen size
  const getAvatarSize = () => {
    if (isXs) return 100;
    if (isSm) return 120;
    if (isMd) return 150;
    return 180; // default for large screens
  };

  const avatarSize = getAvatarSize();

  // State for form values
  const [formData, setFormData] = useState({
    employee: {
      id: null,
      azureId: null,
      fullName: '',
    },
    position: {
      id: null,
      name: ''
    },
    roles: [{
      id: null,
      name: '',
      type: ''
    }],
    grade: {
      id: null,
      name: ''
    },
    country: '',
    city: '',
    customClassification: '',
    useCustomClassification: false
  });

  // Initialize form data from cvData only once on component mount or when CV data first loads
  useEffect(() => {
    if (isDataReady && cvData && !isInitialized.current) {
      const useCustom = cvData.useCustomClassification || false;

      setFormData({
        employee: {
          id: cvData.employee?.id || null,
          azureId: cvData.employee?.azureId || null,
          fullName: cvData.fullName || cvData.employee?.fullName || '',
        },
        position: cvData.position || { id: null, name: '' },
        roles: cvData.roles || [],
        grade: cvData.grade || { id: null, name: '' },
        country: cvData.country || '',
        city: cvData.city || '',
        customClassification: cvData.customClassification || '',
        useCustomClassification: useCustom
      });

      // Set custom classification mode state
      setUseCustomClassification(useCustom);
      setCustomClassification(cvData.customClassification || '');

      // Mark as initialized
      isInitialized.current = true;
    }
  }, [cvData, isDataReady]);

  const handleChange = (field) => (event) => {
    let newFormData = { ...formData };
    
    // Handle nested properties
    if (field.includes('.')) {
      const fieldParts = field.split('.');
      const parentField = fieldParts[0];
      const childField = fieldParts[1];
      
      newFormData = {
        ...formData,
        [parentField]: {
          ...formData[parentField],
          [childField]: event.target.value
        }
      };
    } else {
      // Handle top-level properties
      newFormData = {
        ...formData,
        [field]: event.target.value
      };
    }
    
    setFormData(newFormData);

    // Update the shared context
    updatePersonalDetails(newFormData);
  };
  
  // Function to handle opening the roles modal
  const handleOpenRolesModal = () => {
    console.log('--------------------------------');
    console.log('formData', formData);
    console.log('--------------------------------');
    // Set initial position from formData
    if (formData.position && formData.position.id) {
      setSelectedPositionForRoles({
        id: formData.position.id,
        name: formData.position.name
      });
    }
    
    // Set initial grade from formData
    if (formData.grade && formData.grade.id) {
      setSelectedGrade({
        id: formData.grade.id,
        label: formData.grade.name
      });
    }
    
    // Set initial roles from formData
    if (formData.roles && formData.roles.length > 0) {
      const functionalRoles = formData.roles
        .filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
        .map(role => role.id);
        
      const technicalRoles = formData.roles
        .filter(role => role.type === ROLE_TYPES.TECHNICAL)
        .map(role => role.id);
        
      const organizationalRoles = formData.roles
        .filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
        .map(role => role.id);
      
      setSelectedFunctionalRoles(functionalRoles);
      setSelectedTechnicalRoles(technicalRoles);
      setSelectedOrganizationalRoles(organizationalRoles);
    }
    
    setShowRolesModal(true);
  };
  
  // Function to handle roles update
  const handleRolesUpdate = () => {
    // Create new formData with position, grade and roles
    const newFormData = {
      ...formData,
      position: {
        id: selectedPositionForRoles?.id || null,
        name: selectedPositionForRoles?.label || ''
      },
      grade: {
        id: selectedGrade?.id || null,
        name: selectedGrade?.label || ''
      },
      roles: [
        // Functional roles
        ...selectedFunctionalRoles
          .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.FUNCTIONAL))
          .map(roleId => {
            const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
            return {
              id: roleId,
              name: role?.label || '',
              type: ROLE_TYPES.FUNCTIONAL
            }
          }),
        // Technical roles
        ...selectedTechnicalRoles
          .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.TECHNICAL))
          .map(roleId => {
            const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
            return {
              id: roleId,
              name: role?.label || '',
              type: ROLE_TYPES.TECHNICAL
            }
          }),
        // Organizational roles
        ...selectedOrganizationalRoles
          .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.ORGANIZATIONAL))
          .map(roleId => {
            const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
            return {
              id: roleId,
              name: role?.label || '',
              type: ROLE_TYPES.ORGANIZATIONAL
            }
          })
      ]
    };

    // Update form state
    setFormData(newFormData);

    // Update the shared context
    updatePersonalDetails(newFormData);

    // Close modal
    setShowRolesModal(false);
    
    // Reset modal state
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
    setSelectedGrade(null);
  };

  // Custom classification handlers
  const handleToggleCustomClassification = () => {
    const newUseCustom = !useCustomClassification;
    setUseCustomClassification(newUseCustom);

    // Update form data - keep custom text even when toggling off, just change the flag
    const updatedFormData = {
      ...formData,
      useCustomClassification: newUseCustom
    };

    setFormData(updatedFormData);
    updatePersonalDetails(updatedFormData);
  };

  const handleCustomClassificationChange = (value) => {
    setCustomClassification(value);

    const updatedFormData = {
      ...formData,
      customClassification: value
    };

    setFormData(updatedFormData);
    updatePersonalDetails(updatedFormData);
  };

  // Employee linking functions
  const handleOpenLinkModal = () => {
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = (e) => {
    // Prevent default if this is an event
    if (e) e.preventDefault();
    
    setShowLinkModal(false);
    setSelectedEmployee(null);
  };

  const handleEmployeeChange = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleLinkEmployee = async (e) => {
    // Prevent default form submission behavior
    if (e) e.preventDefault();
    
    if (!selectedEmployee) {
      return;
    }

    try {
      setIsLinking(true);

      // Update the local form data with the selected employee's information
      const updatedFormData = {
        ...formData,
        employee: {
          id: selectedEmployee.id,
          azureId: selectedEmployee.azureId,
          fullName: selectedEmployee.fullName || ''
        }
      };


      // Update form state
      setFormData(updatedFormData);

      // Update the shared context
      updatePersonalDetails(updatedFormData);

      // Close the modal
      setShowLinkModal(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error linking employee:', error);
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkEmployee = async (e) => {
    // Prevent default form submission behavior
    if (e) e.preventDefault();
    
    try {
      setIsUnlinking(true);
      
      // Clear the employee information in the form data
      const updatedFormData = {
        ...formData,
        employee: {
          id: null,
          azureId: null,
          fullName: formData.employee.fullName // Keep the name as it's part of the CV
        }
      };


      // Update form state
      setFormData(updatedFormData);

      // Update the shared context
      updatePersonalDetails(updatedFormData);

    } catch (error) {
      console.error('Error unlinking employee:', error);
    } finally {
      setIsUnlinking(false);
    }
  };

  // Determine if CV is linked to an employee
  const isLinkedToEmployee = cvData?.employee?.id;

  // Early return if no CV data (e.g., after deletion)
  if (!cvData && !isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Typography color="text.secondary">No CV data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <LoadingSpinner message="Loading CV data..." />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography color="error">Error loading CV data: {error}</Typography>
        </Box>
      ) : (
        <Grid 
          container 
          sx={{
            width: '100%',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 2fr'
            },
            gap: '32px',
            '& .MuiGrid-item': {
              padding: '0',
              width: '100%'
            }
          }}
        >
          <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center'
            }}>
              <Box sx={{ 
                width: `${avatarSize}px`, 
                height: `${avatarSize}px`, 
                borderRadius: '50%', 
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2
              }}>
                <EmployeeAvatar employee={formData.employee} size={avatarSize} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Profile Photo
              </Typography>

              {/* Employee Link Status */}
              <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }}>
                {isLinkedToEmployee ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: '#F0F9FF',
                    border: '1px solid #BAE6FD',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    gap: 1
                  }}>
                    <Typography sx={{ 
                      color: '#0369A1', 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1 
                    }}>
                      <FaUser size={12} /> Linked to Employee
                    </Typography>
                    {!disableEmployeeChange && (
                      <Button
                        size="small"
                        onClick={handleUnlinkEmployee}
                        disabled={isUnlinking || readonly}
                        sx={{
                          color: '#0369A1',
                          fontSize: '13px',
                          textTransform: 'none',
                          minWidth: 'auto',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: '1px solid #BAE6FD',
                          '&:hover': {
                            backgroundColor: '#E0F2FE',
                          }
                        }}
                        startIcon={<FaUnlink size={11} />}
                      >
                        {isUnlinking ? 'Unlinking...' : 'Unlink Employee'}
                      </Button>
                    )}
                  </Box>
                ) : (
                  !disableEmployeeChange && (
                    <Button 
                      disabled={readonly}
                      variant="outlined"
                      onClick={handleOpenLinkModal}
                      startIcon={<FaLink />}
                      sx={{
                        color: '#0369A1',
                        borderColor: '#BAE6FD',
                        backgroundColor: '#F0F9FF',
                        fontSize: '13px',
                        '&:hover': {
                          borderColor: '#7DD3FC',
                          backgroundColor: '#E0F2FE',
                        }
                      }}
                    >
                      Link to Employee
                    </Button>
                  )
                )}
              </Box>
            </Box>
          </Grid>
          
          <Grid item>
            <Box component="form" autoComplete="new-password" sx={{ '& .MuiTextField-root': { mb: 3 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Full Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  disabled={readonly}
                  variant="outlined"
                  value={formData.employee.fullName}
                  onChange={handleChange('employee.fullName')}
                  placeholder="Enter your full name"
                  autoComplete="new-password"
                  name="cv_fullname_noautofill"
                  required
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 1 }
                  }}
                />
              </Box>

              <PositionGradeRolesBox
                position={formData.position}
                grade={formData.grade}
                roles={formData.roles}
                onSelectClick={handleOpenRolesModal}
                useCustomClassification={useCustomClassification}
                onToggleCustomClassification={handleToggleCustomClassification}
                customClassification={customClassification}
                onCustomClassificationChange={handleCustomClassificationChange}
                readonly={readonly}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Country
                </Typography>
                <TextField
                  fullWidth
                  disabled={readonly}
                  variant="outlined"
                  value={formData.country}
                  onChange={handleChange('country')}
                  placeholder="Enter your country"
                  autoComplete="new-password"
                  name="cv_country_noautofill"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 1 }
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  City
                </Typography>
                <TextField
                  fullWidth
                  disabled={readonly}
                  variant="outlined"
                  value={formData.city}
                  onChange={handleChange('city')}
                  placeholder="Enter your city"
                  autoComplete="new-password"
                  name="cv_city_noautofill"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 1 }
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
      
      {/* Roles selection modal */}
      <Modal
        isOpen={showRolesModal}
        onClose={() => {
          setShowRolesModal(false);
          setSelectedPositionForRoles(null);
          setSelectedFunctionalRoles([]);
          setSelectedTechnicalRoles([]);
          setSelectedOrganizationalRoles([]);
          setSelectedGrade(null);
        }}
        title="Select Grade, Position & Roles"
        onSubmit={handleRolesUpdate}
        submitText="Update Grade, Position & Roles"
        width="1200px"
        submitButtonProps={{
          sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
        }}
      >
        <RolesSelection
          selectedPositionForRoles={selectedPositionForRoles}
          setSelectedPositionForRoles={setSelectedPositionForRoles}
          selectedFunctionalRoles={selectedFunctionalRoles}
          setSelectedFunctionalRoles={setSelectedFunctionalRoles}
          selectedTechnicalRoles={selectedTechnicalRoles}
          setSelectedTechnicalRoles={setSelectedTechnicalRoles}
          selectedOrganizationalRoles={selectedOrganizationalRoles}
          setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
          initialPosition={formData.position}
          hidePositionInfo={true}
          allowedOrgUnitTypes={[ORG_UNIT_TYPES.DELIVERY]}
          showGrades={true}
          selectedGrade={selectedGrade}
          setSelectedGrade={(grade) => {
            setSelectedGrade({
              id: grade.id,
              label: grade.label
            });
          }}
        />
      </Modal>

      {/* Link Employee Modal */}
      <Modal
        isOpen={showLinkModal}
        onClose={handleCloseLinkModal}
        title="Link CV to Individual"
        onSubmit={handleLinkEmployee}
        submitText={isLinking ? "Linking..." : "Link Individual"}
        submitDisabled={!selectedEmployee || isLinking}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Search and select an individual to link to this CV. This will associate the CV with the individual's profile.
          </Typography>
          <EmployeeSearch
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            width={500}
            height="56px"
            sx={{ width: '100%' }}
            showSelectedAvatar={true}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default PersonalDetails; 